export type Sky = 'clear' | 'partly' | 'cloudy' | 'fog' | 'rain' | 'snow' | 'storm';
export type Place = 'Espoo' | 'Brno';

const W = 64;
const H = 36;

type Run = { x: number; y: number; w: number; h?: number; c: string; o?: number };

function sprite(rows: string[], pal: Record<string, string>, ox: number, oy: number): Run[] {
  const out: Run[] = [];
  rows.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      const ch = row[x]!;
      const c = pal[ch];
      if (!c) {
        x++;
        continue;
      }
      let w = 1;
      while (row[x + w] === ch) w++;
      out.push({ x: ox + x, y: oy + y, w, c });
      x += w;
    }
  });
  return out;
}

function Runs({ runs, className }: { runs: Run[]; className?: string }) {
  return (
    <g className={className}>
      {runs.map((r, i) => (
        <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h ?? 1} fill={r.c} opacity={r.o} />
      ))}
    </g>
  );
}

export function skyOf(code: number | undefined): Sky {
  if (code === undefined) return 'partly';
  if (code <= 1) return 'clear';
  if (code === 2) return 'partly';
  if (code === 3) return 'cloudy';
  if (code === 45 || code === 48) return 'fog';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'snow';
  if (code >= 95) return 'storm';
  return 'rain';
}

const LABELS: Record<number, string> = {
  0: 'Clear sky',
  1: 'Mainly clear',
  2: 'Partly cloudy',
  3: 'Overcast',
  45: 'Fog',
  48: 'Rime fog',
  51: 'Light drizzle',
  53: 'Drizzle',
  55: 'Dense drizzle',
  56: 'Freezing drizzle',
  57: 'Freezing drizzle',
  61: 'Light rain',
  63: 'Rain',
  65: 'Heavy rain',
  66: 'Freezing rain',
  67: 'Freezing rain',
  71: 'Light snow',
  73: 'Snow',
  75: 'Heavy snow',
  77: 'Snow grains',
  80: 'Rain showers',
  81: 'Rain showers',
  82: 'Violent rain showers',
  85: 'Snow showers',
  86: 'Heavy snow showers',
  95: 'Thunderstorm',
  96: 'Thunderstorm with hail',
  99: 'Thunderstorm with hail',
};

export function labelOf(code: number | undefined): string {
  return code === undefined ? 'Unknown' : (LABELS[code] ?? 'Unknown');
}

const SKY: Record<Sky, { day: string[]; night: string[] }> = {
  clear: { day: ['#3f8fe0', '#5aa6ee', '#7cbcf5', '#a6d4fa'], night: ['#0d1533', '#132049', '#1b2c5e', '#263a73'] },
  partly: { day: ['#4a93dc', '#64a9ea', '#86bff2', '#addaf8'], night: ['#0f1736', '#15224c', '#1d2d60', '#283b74'] },
  cloudy: { day: ['#7d8a9e', '#8e9aac', '#a0abbb', '#b3bcc9'], night: ['#1a2030', '#20283a', '#283146', '#303a52'] },
  fog: { day: ['#a3acb8', '#b2bac4', '#c2c8d0', '#d2d7dd'], night: ['#2a3040', '#30374a', '#373f54', '#3f485e'] },
  rain: { day: ['#56657c', '#64738a', '#738298', '#8391a6'], night: ['#151b29', '#1b2232', '#222a3c', '#2a3346'] },
  snow: { day: ['#9aa9bf', '#aab7cb', '#bbc6d6', '#cdd5e1'], night: ['#1f2638', '#262e42', '#2e374d', '#374158'] },
  storm: { day: ['#363c4e', '#41475b', '#4c5368', '#585f75'], night: ['#0f121b', '#141822', '#1a1f2b', '#212734'] },
};

const CLOUD_TONES: Record<Sky, { day: [string, string]; night: [string, string] }> = {
  clear: { day: ['#f5f8fc', '#cdd6e2'], night: ['#46506a', '#343c52'] },
  partly: { day: ['#f5f8fc', '#cdd6e2'], night: ['#46506a', '#343c52'] },
  cloudy: { day: ['#d5dae1', '#aab2be'], night: ['#3c4458', '#2d3446'] },
  fog: { day: ['#e1e5ea', '#c4cad3'], night: ['#4a5266', '#3a4154'] },
  rain: { day: ['#9aa3b1', '#7a8391'], night: ['#323a4c', '#262d3c'] },
  snow: { day: ['#e4e9f0', '#c3ccd8'], night: ['#4b5670', '#3a4459'] },
  storm: { day: ['#5c6272', '#454a58'], night: ['#2a2f3b', '#1f232d'] },
};

const SUN = ['..yyy..', '.yYYYy.', 'yYYYYYy', 'yYYYYYy', 'yYYYYYy', '.yYYYy.', '..yyy..'];
const MOON = ['..mmm..', '.mmM...', 'mmM....', 'mmM....', 'mmM....', '.mmM...', '..mmm..'];
const CLOUD_A = ['.....WWW......', '...WWWWWWW....', '..WWWWWWWWWW..', '.WWWWWWWWWWWW.', 'WWWWWWWWWWWWWW', '.SSSSSSSSSSSS.'];
const CLOUD_B = ['...WWW....', '.WWWWWWW..', 'WWWWWWWWWW', '.SSSSSSSS.'];
const BOLT = ['..LL.', '.LL..', 'LLLL.', '..LL.', '.LL..', '.L...', 'L....'];
const PINE = ['..g..', '..g..', '.ggg.', '.gGg.', 'ggggg', '.ggg.', 'gggGg', 'ggggg', 'ggggg', '..t..'];
const ROCKS = ['......rrr.........', '...rrrrrrrr.......', '.rrrrrrrrrrrr.....', 'rrrrrrrrrrrrrrr...', 'RRRRRRRRRRRRRRRRR.'];
const ISLE = ['...rrrr...', '.rrrrrrrr.', 'RRRRRRRRRR'];
const PETROV = [
  '....x......x.........',
  '....x......x.........',
  '...xxx....xxx........',
  '...xxx....xxx........',
  '...xwx....xwx........',
  '...xxx....xxx........',
  '...xxx....xxx........',
  '..xxxxxxxxxxxx.......',
  '..xxxxxxxxxxxx.......',
  '..xxwxxwxxwxxx..xxx..',
  '..xxxxxxxxxxxxxxxxx..',
  '.xxxxxxxxxxxxxxxxxxx.',
  'xxxxxxxxxxxxxxxxxxxxx',
];
const STARS: [number, number, string][] = [
  [5, 3, '#f4f1d0'], [12, 7, '#9fb2e6'], [20, 2, '#f4f1d0'], [27, 9, '#f4f1d0'], [35, 4, '#9fb2e6'], [41, 11, '#f4f1d0'],
  [58, 14, '#9fb2e6'], [16, 13, '#f4f1d0'], [30, 16, '#9fb2e6'], [62, 4, '#f4f1d0'], [8, 18, '#9fb2e6'], [23, 20, '#f4f1d0'],
];

function skyRuns(colors: string[]): Run[] {
  const bh = 7;
  const out: Run[] = colors.map((c, i) => ({ x: 0, y: i * bh, w: W, h: i === colors.length - 1 ? H - i * bh : bh, c }));
  for (let i = 1; i < colors.length; i++) {
    for (let x = 0; x < W; x += 2) out.push({ x, y: i * bh, w: 1, c: colors[i - 1]! });
    for (let x = 1; x < W; x += 2) out.push({ x, y: i * bh - 1, w: 1, c: colors[i]! });
  }
  return out;
}

function fallRuns(count: number, step: [number, number], size: [number, number], c: string): Run[] {
  const out: Run[] = [];
  for (let i = 0; i < count; i++) {
    const x = (i * step[0] + 3) % W;
    const y = (i * step[1]) % H;
    out.push({ x, y, w: size[0], h: size[1], c }, { x, y: y - H, w: size[0], h: size[1], c });
  }
  return out;
}

function weatherRuns(sky: Sky, day: boolean): { back: Run[]; clouds: Run[]; fall: Run[]; fallKind: 'rain' | 'snow' | null; bolt: Run[] } {
  const [cw, cs] = CLOUD_TONES[sky][day ? 'day' : 'night'];
  const cloudPal = { W: cw, S: cs };
  const back: Run[] = [];
  if (!day && (sky === 'clear' || sky === 'partly')) STARS.forEach(([x, y, c]) => back.push({ x, y, w: 1, c }));
  if (sky === 'clear' || sky === 'partly') {
    back.push(...(day ? sprite(SUN, { y: '#f7b733', Y: '#ffd84d' }, 49, 4) : sprite(MOON, { m: '#f2ecd0', M: '#d8d0ac' }, 50, 4)));
    if (day) {
      for (const [dx, dy] of [[0, -5], [0, 5], [-5, 0], [5, 0], [-4, -4], [4, -4], [-4, 4], [4, 4]] as const) {
        back.push({ x: 52 + dx, y: 7 + dy, w: 1, c: '#ffe27a' });
      }
    }
  }
  const clouds: Run[] = [];
  if (sky === 'partly') clouds.push(...sprite(CLOUD_A, cloudPal, 36, 9), ...sprite(CLOUD_B, cloudPal, 6, 5));
  if (sky === 'cloudy' || sky === 'rain' || sky === 'snow' || sky === 'storm' || sky === 'fog') {
    clouds.push(
      ...sprite(CLOUD_A, cloudPal, 2, 3),
      ...sprite(CLOUD_A, cloudPal, 28, 1),
      ...sprite(CLOUD_B, cloudPal, 50, 6),
      ...sprite(CLOUD_B, cloudPal, 17, 9),
      ...sprite(CLOUD_A, cloudPal, 40, 11)
    );
  }
  if (sky === 'fog') {
    const fogC = day ? '#eef1f4' : '#5a6378';
    for (const [y, gap] of [[15, 5], [18, 0], [21, 9], [24, 3]] as const) {
      for (let x = -gap; x < W; x += 22) clouds.push({ x: Math.max(0, x), y, w: 16, h: 1, c: fogC, o: 0.7 });
    }
  }
  let fall: Run[] = [];
  let fallKind: 'rain' | 'snow' | null = null;
  if (sky === 'rain' || sky === 'storm') {
    fall = fallRuns(34, [11, 7], [1, 2], day ? '#a9d4ff' : '#5f7fae');
    fallKind = 'rain';
  } else if (sky === 'snow') {
    fall = fallRuns(28, [9, 5], [1, 1], '#ffffff');
    fallKind = 'snow';
  }
  const bolt = sky === 'storm' ? sprite(BOLT, { L: '#ffe45c' }, 27, 14) : [];
  return { back, clouds, fall, fallKind, bolt };
}

function espooGround(day: boolean, snowy: boolean): Run[] {
  const sea = day ? ['#2f7fb8', '#25689a', '#1d557f'] : ['#13294a', '#10223d', '#0c1b31'];
  const out: Run[] = [
    { x: 0, y: 28, w: W, h: 3, c: sea[0]! },
    { x: 0, y: 31, w: W, h: 3, c: sea[1]! },
    { x: 0, y: 34, w: W, h: 2, c: sea[2]! },
  ];
  const wave = day ? '#8cc7ee' : '#2b4d78';
  for (const [x, y] of [[20, 29], [34, 31], [44, 29], [27, 33], [56, 32], [12, 34], [39, 34]] as const) out.push({ x, y, w: 2, c: wave });
  const rock = { r: snowy ? '#e9eef4' : day ? '#7a7f88' : '#3a3e47', R: day ? '#5f646d' : '#2c2f37' };
  out.push(...sprite(ROCKS, rock, 0, 25), ...sprite(ISLE, rock, 51, 27));
  const pine = { g: day ? '#1f6b3a' : '#123a22', G: snowy ? '#e9eef4' : day ? '#2f8a4c' : '#1a4a2c', t: day ? '#5a3b22' : '#2e1f13' };
  out.push(...sprite(PINE, pine, 1, 16), ...sprite(PINE, pine, 6, 15), ...sprite(PINE, pine, 11, 17), ...sprite(PINE, pine, 54, 18));
  return out;
}

function brnoGround(day: boolean, snowy: boolean): Run[] {
  const hill = snowy ? '#e4eaf2' : day ? '#5c8a3e' : '#22351a';
  const hillTop = snowy ? '#ffffff' : day ? '#7fb456' : '#2f4724';
  const out: Run[] = [];
  for (let x = 0; x < W; x++) {
    const top = 27 + Math.round(2 * Math.sin(x / 9) + Math.sin(x / 4) * 0.6);
    out.push({ x, y: top, w: 1, h: H - top, c: hill }, { x, y: top, w: 1, c: hillTop });
  }
  const city = day ? '#4a4f5c' : '#1c1f27';
  const win = day ? '#6a7180' : '#ffd36b';
  for (const [x, w, h] of [[30, 6, 6], [36, 5, 8], [41, 7, 5], [48, 6, 7], [54, 5, 9], [59, 5, 6]] as const) {
    out.push({ x, y: H - h, w, h, c: city });
    if (snowy) out.push({ x, y: H - h, w, c: '#e4eaf2' });
    for (let wy = H - h + 2; wy < H - 1; wy += 2) {
      for (let wx = x + 1; wx < x + w - 1; wx += 2) {
        if ((wx * 7 + wy * 3) % 5 !== 0) out.push({ x: wx, y: wy, w: 1, c: win });
      }
    }
  }
  out.push(...sprite(PETROV, { x: day ? '#3f4350' : '#15171e', w: win }, 6, 18));
  return out;
}

export function PixelScene({ place, sky, day, label }: { place: Place; sky: Sky; day: boolean; label: string }) {
  const { back, clouds, fall, fallKind, bolt } = weatherRuns(sky, day);
  const snowy = sky === 'snow';
  const ground = place === 'Espoo' ? espooGround(day, snowy) : brnoGround(day, snowy);
  return (
    <svg className="scene" viewBox={`0 0 ${W} ${H}`} shapeRendering="crispEdges" role="img" aria-label={label}>
      <Runs runs={skyRuns(SKY[sky][day ? 'day' : 'night'])} />
      <Runs runs={back} />
      <Runs runs={clouds} />
      {fallKind && <Runs runs={fall} className={`fall fall-${fallKind}`} />}
      {bolt.length > 0 && <Runs runs={bolt} className="bolt" />}
      <Runs runs={ground} />
    </svg>
  );
}
